import React from "react";
import { ArrowUpRight, Check } from "lucide-react";
import { SERVICES } from "../../lib/site-data";

/**
 * Alternating image/text rows  Teladoc rhythm.
 * Even-indexed rows place text on the left, image on the right.
 */
export default function ServiceRows() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-cream py-24 lg:py-32"
    >
      <div className="container-x">
        <div className="max-w-3xl mb-16 lg:mb-24">
          <p className="text-[12.5px] font-semibold uppercase tracking-[0.18em] text-forest">
            The care you need
          </p>
          <h2 className="mt-4 font-display text-ink text-[36px] sm:text-[44px] lg:text-[56px] font-extrabold leading-[1.05] tracking-tight text-balance">
            All in one place  from a quick check-in to a lifetime plan.
          </h2>
          <p className="mt-5 text-[17px] text-ink/65 max-w-2xl leading-relaxed">
            Six connected services, one continuous record. Move from a WhatsApp message to a video consult to a lab visit without losing context.
          </p>
        </div>

        <div className="space-y-24 lg:space-y-36">
          {SERVICES.map((s, idx) => {
            const reverse = s.align === "left"; // text left, image right by default; reverse means image left
            return (
              <article
                key={s.id}
                id={s.id}
                data-testid={`service-row-${s.id}`}
                className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 items-center"
              >
                <div
                  className={`lg:col-span-6 ${
                    reverse ? "lg:order-2" : "lg:order-1"
                  }`}
                >
                  <p className="text-[12.5px] font-semibold uppercase tracking-[0.18em] text-forest">
                    {s.eyebrow}
                  </p>
                  <h3 className="mt-3 font-display text-ink text-[28px] sm:text-[34px] lg:text-[42px] leading-[1.08] font-extrabold tracking-tight text-balance">
                    {s.title}
                  </h3>
                  <p className="mt-5 text-[16.5px] text-ink/70 leading-relaxed max-w-xl">
                    {s.body}
                  </p>
                  <ul className="mt-7 space-y-3">
                    {s.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-3 text-[15.5px] text-ink/85"
                      >
                        <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-forest/10 text-forest">
                          <Check className="h-3 w-3" strokeWidth={3} />
                        </span>
                        {b}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8">
                    <a
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`service-cta-${s.id}`}
                      className="group inline-flex items-center gap-1.5 text-forest font-semibold underline-offset-4 hover:underline"
                    >
                      Start on WhatsApp
                      <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </a>
                  </div>
                </div>

                <div
                  className={`lg:col-span-6 ${
                    reverse ? "lg:order-1" : "lg:order-2"
                  }`}
                >
                  <div className="relative">
                    <div
                      className="absolute -inset-3 -z-10 rounded-[36px] bg-clay/10"
                      aria-hidden="true"
                      style={{
                        transform: idx % 2 === 0 ? "rotate(-1.2deg)" : "rotate(1.2deg)",
                      }}
                    />
                    <img
                      src={s.image}
                      alt={s.image_alt}
                      loading="lazy"
                      className="block w-full rounded-3xl object-cover aspect-[5/4] shadow-[0_24px_60px_rgba(14,35,51,0.12)]"
                    />
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
